import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
} from '@mui/material';
import Autocomplete, { AutocompleteProps, createFilterOptions } from '@mui/material/Autocomplete';
import useAxios from 'axios-hooks';
import { isEmpty } from 'lodash';
import debounce from 'lodash/debounce';

import strings, { url } from 'constants/index';
import { ColumnIds } from 'enums';
import {
  getCompetitorNameLines,
  isPlaceholderCompetitor,
} from 'features/rounds/components/Draws/DrawsUtils';
import { colors } from 'styles/tokens';
import { buildRequestParams } from 'utils/pagination.utils';

type AutocompleteOption = Pick<Entity, 'id' | 'name'> & {
  officialName?: string;
  isTbd?: boolean;
};

const getOptionDisplayName = (option?: AutocompleteOption | null): string =>
  option?.name ?? option?.officialName ?? '';

const formatStackedDisplayName = (displayName: string): string =>
  getCompetitorNameLines(displayName).join('\n');

// Match against the original "Partner1/Partner2" name so paste-search still works
// while getOptionLabel can show partners stacked on separate lines.
const filterByOriginalDisplayName = createFilterOptions<AutocompleteOption>({
  stringify: option => getOptionDisplayName(option),
});

const filterStackedSlashOptions = (
  options: AutocompleteOption[],
  state: Parameters<typeof filterByOriginalDisplayName>[1],
) =>
  filterByOriginalDisplayName(options, {
    ...state,
    // Users paste "A/B"; stacked labels use a newline — treat them the same for matching.
    inputValue: state.inputValue.replace(/\n/g, '/'),
  });

const normalizeAutocompleteOption = <T extends AutocompleteOption>(option: T): T => {
  if (!option) {
    return option;
  }

  const displayName = getOptionDisplayName(option);

  return displayName && !option.name ? { ...option, name: displayName } : option;
};

const normalizeAutocompleteOptions = <T extends AutocompleteOption>(options: T[] = []): T[] =>
  options.map(normalizeAutocompleteOption);
/** Extends AutcompleteProps without freesolo behaviour
 * remove the options and renderInput props since they are been filled from component state
 */

export interface AsyncAutocompleteProps
  extends Omit<
    AutocompleteProps<AutocompleteOption, boolean, boolean, false>,
    'options' | 'renderInput'
  > {
  dataSource: string | Array<AutocompleteOption>;
  onChange: (value) => void;
  label?: string;
  defaultValue?: AutocompleteOption | Array<AutocompleteOption>;
  defaultFilters?: Array<PaginationFilter>;
  searchTermMinLength?: number;
  searchTermColumnId?: string;
  textFieldProps?: (
    | Omit<StandardTextFieldProps, 'onChange'>
    | Omit<FilledTextFieldProps, 'onChange'>
    | Omit<OutlinedTextFieldProps, 'onChange'>
  ) & { 'data-testid'?: string };
  required?: boolean;
  externalOptions?: Array<AutocompleteOption>;
  dynamicSearch?: Array<{ columnId: string; type: string }>;
  hideId?: boolean;
  noOrder?: boolean;
  noHeader?: boolean;
  /** When true, names like "A/B" render on separate lines in the selected value and options. */
  stackSlashNames?: boolean;
}

const AsyncAutocomplete = ({
  dataSource,
  defaultValue,
  disabled,
  defaultFilters,
  label,
  searchTermMinLength = 1,
  searchTermColumnId = ColumnIds.Name,
  required,
  textFieldProps,
  onChange,
  multiple,
  externalOptions,
  dynamicSearch,
  hideId = false,
  noOrder = false,
  noHeader = false,
  stackSlashNames = false,
  ...autocompleteProps
}: AsyncAutocompleteProps) => {
  const {
    error: textFieldPropsError,
    helperText: textFieldPropsHelperText,
    ...restTextFieldProps
  } = textFieldProps || {};
  const [searchParams, setSearchParams] = useState({});
  const [{ data }] = useAxios(
    {
      url: dataSource as string,
      params: searchParams,
    },
    { manual: Object.keys(searchParams).length === 0 || typeof dataSource !== 'string' },
  );
  const [options, setOptions] = useState<AutocompleteOption[]>(
    typeof dataSource === 'string'
      ? externalOptions
        ? externalOptions
        : []
      : normalizeAutocompleteOptions(dataSource),
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [value, setValue] = useState(
    multiple
      ? normalizeAutocompleteOptions(defaultValue as AutocompleteOption[])
      : normalizeAutocompleteOption(defaultValue as AutocompleteOption) || (multiple ? [] : null),
  );
  const isSearchTermANumber = !Number.isNaN(parseInt(searchTerm));
  useEffect(() => {
    const normalizedDefaultValue = multiple
      ? normalizeAutocompleteOptions(defaultValue as AutocompleteOption[])
      : normalizeAutocompleteOption(defaultValue as AutocompleteOption);

    setValue(normalizedDefaultValue || (multiple ? [] : null));
    if (!defaultValue && typeof dataSource === 'string') {
      setOptions(externalOptions ? externalOptions : []);
      setSearchTerm('');
    }
  }, [defaultValue, dataSource, multiple, externalOptions]);

  useEffect(() => {
    if (data) {
      const normalizedItems = normalizeAutocompleteOptions(data.items);
      setOptions(
        externalOptions
          ? [
              ...externalOptions.filter(option =>
                getOptionDisplayName(option).toLowerCase().includes(searchTerm.toLowerCase()),
              ),
              ...normalizedItems,
            ]
          : normalizedItems,
      );
    }
  }, [data, dataSource, externalOptions, searchTerm]);

  const getNoOptionsText = (): string => {
    if (searchTerm) {
      if (searchTerm.length < searchTermMinLength) {
        return strings.term_too_short;
      } else {
        return `${strings.no_results_for_term} ${searchTerm}`;
      }
    }

    if (Array.isArray(dataSource) && !dataSource.length) {
      return strings.no_options_avalable;
    }

    return strings.type_search_term;
  };

  const dynamicColumnId = useMemo(() => {
    if (dynamicSearch && !isEmpty(dynamicSearch)) {
      if (!isSearchTermANumber) {
        return dynamicSearch.find(o => o.type === 'string').columnId;
      } else {
        return dynamicSearch.find(o => o.type === 'number').columnId;
      }
    } else {
      return searchTermColumnId;
    }
  }, [dynamicSearch, isSearchTermANumber, searchTermColumnId]);

  useEffect(() => {
    if (typeof dataSource === 'string') {
      if (searchTerm?.length >= searchTermMinLength) {
        setSearchParams(
          buildRequestParams({
            pageSize: 100,
            sortBy: [{ columnId: dynamicColumnId, order: 'asc' }],
            filters: [
              ...(defaultFilters || []),
              ...(searchTerm
                ? [
                    {
                      columnId: dynamicColumnId,
                      value: searchTerm,
                      condition: isSearchTermANumber ? 'in' : 'contains',
                    },
                  ]
                : []),
            ],
          }),
        );
        setSearchTerm('');
      } else {
        setOptions(externalOptions ? externalOptions : []);
      }
    } else {
      setOptions(normalizeAutocompleteOptions(dataSource));
    }
  }, [
    dataSource,
    searchTerm,
    searchTermMinLength,
    defaultFilters,
    searchTermColumnId,
    dynamicColumnId,
    isSearchTermANumber,
    externalOptions,
  ]);

  const delayedHandleInputChange = debounce(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(evt.target.value || '');
      if (!evt.target.value && typeof dataSource === 'string') {
        setOptions(externalOptions ? externalOptions : []);
      }
    },
    typeof dataSource === 'string' ? 250 : 0,
  );

  const getExtraInfo = useCallback(
    (option: AutocompleteOption) => {
      const optionAsAny = option as any;
      const isPlayerCompetitor = dataSource === url.players;
      const isSeasonCompetitor = optionAsAny.competitorType !== undefined;
      if (isPlayerCompetitor) {
        return optionAsAny.person?.locality?.name;
      }

      if (isSeasonCompetitor) {
        return optionAsAny.locality?.name;
      }
    },
    [dataSource],
  );

  const getOptionLabel = useMemo(
    () => (option: AutocompleteOption) => {
      const displayName = getOptionDisplayName(option);
      const labelName = stackSlashNames ? formatStackedDisplayName(displayName) : displayName;
      const extraInfo = getExtraInfo(option);

      if (hideId) {
        return labelName;
      }

      if (dynamicSearch) {
        return (
          (isSearchTermANumber
            ? option.id?.toString()
            : option.id
              ? `${labelName} - ${option.id}${extraInfo ? ` - ${extraInfo}` : ''}`
              : labelName) || ''
        );
      }
      if (isEmpty(option) || !displayName) {
        return '';
      }
      // Keep draw boxes readable: stack partner names and omit the id suffix there.
      if (stackSlashNames) {
        return labelName;
      }
      return `${labelName} - ${option.id}${extraInfo ? ` - ${extraInfo}` : ''}` || '';
    },
    [dynamicSearch, getExtraInfo, isSearchTermANumber, hideId, stackSlashNames],
  );

  const selectedDisplayName = getOptionDisplayName(
    (Array.isArray(value) ? value[0] : value) as AutocompleteOption,
  );
  const useStackedInput = stackSlashNames && getCompetitorNameLines(selectedDisplayName).length > 1;

  const orderedOptions = useMemo(
    () =>
      noOrder
        ? options
        : [...options].sort(
            (a, b) =>
              -getOptionDisplayName(b).charAt(0).localeCompare(getOptionDisplayName(a).charAt(0)),
          ),
    [noOrder, options],
  );

  useEffect(() => delayedHandleInputChange.cancel);
  return (
    <Autocomplete
      {...autocompleteProps}
      disabled={disabled}
      isOptionEqualToValue={(option: AutocompleteOption, value: AutocompleteOption) =>
        value.id ? option.id === value.id : true
      }
      getOptionLabel={getOptionLabel}
      filterOptions={stackSlashNames ? filterStackedSlashOptions : undefined}
      options={orderedOptions}
      onChange={(event, value) => {
        const normalizedValue = multiple
          ? normalizeAutocompleteOptions(value as AutocompleteOption[])
          : normalizeAutocompleteOption(value as AutocompleteOption);

        onChange(normalizedValue);
        setValue(normalizedValue);
      }}
      onBlur={() => {
        if (!value) {
          setSearchTerm('');
        }
      }}
      multiple={multiple}
      noOptionsText={getNoOptionsText()}
      value={value}
      groupBy={noHeader ? undefined : option => getOptionDisplayName(option).charAt(0)}
      renderInput={params => (
        <TextField
          {...params}
          onChange={delayedHandleInputChange}
          label={label}
          required={required}
          error={!!textFieldPropsError}
          helperText={textFieldPropsHelperText}
          variant="standard"
          multiline={useStackedInput}
          maxRows={useStackedInput ? 2 : undefined}
          {...restTextFieldProps}
          sx={{
            ...(restTextFieldProps as { sx?: object })?.sx,
            ...(useStackedInput
              ? {
                  '& .MuiInputBase-root': {
                    alignItems: 'center',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '12px',
                    lineHeight: 1.25,
                    padding: '2px 0',
                    // Keep each partner on one row; clip overflow instead of wrapping
                    // onto the next partner line.
                    whiteSpace: 'pre',
                    overflow: 'hidden',
                    overflowWrap: 'normal',
                    wordBreak: 'keep-all',
                  },
                }
              : {}),
          }}
        />
      )}
      renderOption={(props, option) => {
        let match,
          index = null;
        const displayName = getOptionDisplayName(option);
        const nameLines = stackSlashNames ? getCompetitorNameLines(displayName) : [displayName];
        // Regex need to be defined inside each clousure
        const tester = new RegExp(searchTerm.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&'), 'ig');
        const extraInfo = getExtraInfo(option);
        const result = tester.exec(displayName);
        if (result) {
          match = result[0];
          index = result.index;
        }

        if (stackSlashNames && nameLines.length > 1) {
          return (
            <li {...props}>
              <Box
                sx={{
                  color: isPlaceholderCompetitor(option) ? colors.lightRed : 'unset',
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.25,
                }}
              >
                {nameLines.map(line => (
                  <Box component="span" key={line}>
                    {line}
                  </Box>
                ))}
                {!hideId && option.id ? (
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.85em' }}>
                    {option.id}
                    {extraInfo ? ` - ${extraInfo}` : ''}
                  </Box>
                ) : null}
              </Box>
            </li>
          );
        }

        const parts = displayName.split('').map((char, i) => ({
          text: char,
          id: option.id,
          highlight: match
            ? i === Number(index) || (i > Number(index) && i < Number(index) + match.length)
            : false,
          extraInfo,
        }));

        return (
          <li {...props}>
            <Box
              sx={{
                color: isPlaceholderCompetitor(option) ? colors.lightRed : 'unset',
              }}
            >
              {parts.map((part, index) => (
                <Box
                  component="span"
                  key={index}
                  sx={{
                    fontWeight: part.highlight ? 700 : 400,
                  }}
                >
                  {part.text}
                </Box>
              ))}
              {!hideId && parts.some(part => part.id) && ` - ${parts.find(part => part.id)?.id}`}
              {parts.some(part => part.extraInfo) &&
                ` - ${parts.find(part => part.extraInfo)?.extraInfo}`}
            </Box>
          </li>
        );
      }}
    />
  );
};

export default AsyncAutocomplete;
